import { Preferences } from '@capacitor/preferences';
import type { StorageAdapter } from '@lexicon/core';
import { isNative } from './platform';

/** Web-only storage keys. Core owns the shared ones in `KEYS`. */
export const WEB_KEYS = {
  playerName: 'lexicon.playerName',
  /**
   * The chosen language. Not itself scoped by language — it *is* the
   * scope, and it is what stage one of `AppProvider`'s boot reads first.
   */
  language: 'lexicon.language',
  /** Journey progress (spec §10.3): rung, run wins, completions, best rung. */
  journey: 'lexicon.journey',
  /** The one in-progress journey game — a single key, like the daily's. */
  journeyGame: 'lexicon.journeyGame',
  /** One session per challenge code. */
  challengeGame: (code: string) => `lexicon.challengeGame.${code}`,
  /** Marks that the once-a-day anonymous play ping (spec §11) has fired for `dateKey`. */
  pinged: (dateKey: string) => `lexicon.pinged.${dateKey}`,
} as const;

/** Storage key marking that today's anonymous play ping has fired (spec §11). */
export function pingedKey(dateKey: string): string {
  return WEB_KEYS.pinged(dateKey);
}

function detectLocalStorage(): Storage | null {
  try {
    const store = window.localStorage;
    const probe = '__lexicon_probe__';
    store.setItem(probe, '1');
    store.removeItem(probe);
    return store;
  } catch {
    return null;
  }
}

/**
 * `StorageAdapter` over `window.localStorage`. Every call is guarded:
 * private browsing, disabled storage and quota errors must degrade to
 * "nothing stored" rather than crash the game (spec §7).
 */
export class LocalStorageAdapter implements StorageAdapter {
  private readonly backing: Storage | null;

  constructor(backing: Storage | null = detectLocalStorage()) {
    this.backing = backing;
  }

  async get(key: string): Promise<string | null> {
    try {
      return this.backing?.getItem(key) ?? null;
    } catch {
      return null;
    }
  }

  async set(key: string, value: string): Promise<void> {
    try {
      this.backing?.setItem(key, value);
    } catch {
      /* storage full or unavailable — drop the write */
    }
  }

  async remove(key: string): Promise<void> {
    try {
      this.backing?.removeItem(key);
    } catch {
      /* ignore */
    }
  }
}

/**
 * `StorageAdapter` over Capacitor Preferences (spec §2) — the iOS twin of
 * `LocalStorageAdapter`, backed by `UserDefaults` so state survives an app
 * update. Same contract and the same failure rule as the web adapter: a
 * rejected bridge call degrades to "nothing stored" rather than crashing
 * the game (spec §7).
 */
export class PreferencesStorage implements StorageAdapter {
  async get(key: string): Promise<string | null> {
    try {
      const { value } = await Preferences.get({ key });
      return value ?? null;
    } catch {
      return null;
    }
  }

  async set(key: string, value: string): Promise<void> {
    try {
      await Preferences.set({ key, value });
    } catch {
      /* bridge unavailable — drop the write */
    }
  }

  async remove(key: string): Promise<void> {
    try {
      await Preferences.remove({ key });
    } catch {
      /* ignore */
    }
  }
}

let platformStorage: StorageAdapter | null = null;

/**
 * The adapter the running app uses: Preferences inside the shell,
 * `localStorage` in a browser. Tests inject their own instead.
 *
 * Memoised deliberately, not for speed: `AppProvider` takes this as a
 * default prop and keys its boot effect off the adapter's identity, so a
 * fresh instance per call would re-run the boot read on every render.
 */
export function getPlatformStorage(): StorageAdapter {
  if (platformStorage === null) {
    platformStorage = isNative() ? new PreferencesStorage() : new LocalStorageAdapter();
  }
  return platformStorage;
}
